/** Label plus control plus description plus error. Never render a bare input. */

import {
  cloneElement,
  isValidElement,
  useId,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "@/lib/cn";

import "./Field.css";

/** What a render-prop child is handed, so it can own its own id. */
export interface FieldRender {
  readonly id: string;
  readonly required: boolean;
}

export interface FieldProps {
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly required?: boolean;
  readonly error?: ReactNode;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode | ((render: FieldRender) => ReactNode);
}

/** Give a plain element child the id and description the label already points at. */
function adopt(node: ReactNode, id: string, describedBy: string | undefined, required: boolean) {
  if (!isValidElement<Record<string, unknown>>(node)) return node;
  const own = node.props;
  const extra: Record<string, unknown> = {};
  if (own["id"] === undefined) extra["id"] = id;
  if (describedBy !== undefined && own["aria-describedby"] === undefined) {
    extra["aria-describedby"] = describedBy;
  }
  if (required && own["required"] === undefined) extra["required"] = true;
  return cloneElement(node, extra);
}

export function Field({
  label,
  description,
  required = false,
  error,
  className,
  style,
  children,
}: FieldProps) {
  const id = useId();
  const descriptionId = description !== undefined ? `${id}-description` : undefined;
  const errorId = error !== undefined ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter((part) => part !== undefined).join(" ");

  const control =
    typeof children === "function"
      ? children({ id, required })
      : adopt(children, id, describedBy === "" ? undefined : describedBy, required);

  return (
    <div className={cn("al-field", className)} style={style}>
      <label className="al-field-label" htmlFor={id}>
        {label}
        {required ? (
          <span className="al-field-required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {description !== undefined ? (
        <div className="al-field-description" id={descriptionId}>
          {description}
        </div>
      ) : null}
      <div className="al-field-control">{control}</div>
      {error !== undefined ? (
        <div className="al-field-error" id={errorId} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
